// PWA hooks for React components

import { useState, useEffect, useCallback } from 'react'
import { pwaService, PWAInstallState, ServiceWorkerState } from '../services/pwaService'

// Hook for managing app installation
export const usePWAInstall = () => {
  const [installState, setInstallState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    isStandalone: false,
    canInstall: false,
    platform: 'unknown'
  })
  
  const [isInstalling, setIsInstalling] = useState(false)

  useEffect(() => {
    // Update install state
    const updateState = () => {
      setInstallState(pwaService.getPWAInstallState())
    }

    // Set up install prompt callback
    pwaService.onInstallPromptReady((canInstall) => {
      setInstallState(prev => ({
        ...prev,
        isInstallable: canInstall,
        canInstall: canInstall && !prev.isInstalled
      }))
    })

    // Check initial state
    updateState()

    // Listen for app installed event
    const handleAppInstalled = () => updateState()
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!installState.canInstall || isInstalling) {
      return false
    }

    setIsInstalling(true)
    
    try {
      const accepted = await pwaService.promptInstall()
      
      if (accepted) {
        setInstallState(prev => ({
          ...prev,
          isInstalled: true,
          canInstall: false,
          isInstallable: false
        }))
      }
      
      return accepted
    } finally {
      setIsInstalling(false)
    }
  }, [installState.canInstall, isInstalling])

  return {
    ...installState,
    isInstalling,
    promptInstall
  }
}

// Hook for managing service worker
export const useServiceWorker = () => {
  const [swState, setSwState] = useState<ServiceWorkerState>({
    isSupported: false,
    isRegistered: false,
    isActivated: false,
    registration: null,
    hasUpdate: false
  })
  
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    const initializeServiceWorker = async () => {
      const state = await pwaService.registerServiceWorker()
      setSwState(state)
    }

    // Set up update available callback
    pwaService.onUpdateAvailable(() => {
      setSwState(prev => ({ ...prev, hasUpdate: true }))
    })

    initializeServiceWorker()
  }, [])

  const activateUpdate = useCallback(async () => {
    if (!swState.hasUpdate || isUpdating) {
      return
    }

    setIsUpdating(true)
    
    try {
      await pwaService.activateUpdate()
      // Page will reload automatically after activation
    } catch (error) {
      console.error('Failed to activate update:', error)
      setIsUpdating(false)
    }
  }, [swState.hasUpdate, isUpdating])

  const checkForUpdates = useCallback(async () => {
    const hasUpdate = await pwaService.checkForUpdates()
    setSwState(prev => ({ ...prev, hasUpdate }))
    return hasUpdate
  }, [])

  return {
    ...swState,
    isUpdating,
    activateUpdate,
    checkForUpdates
  }
}

// Hook for managing offline functionality
export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [hasOfflineData, setHasOfflineData] = useState(false)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const storeOfflineData = useCallback(async (key: string, data: any) => {
    await pwaService.storeOfflineData(key, data)
    setHasOfflineData(true)
  }, [])

  const getOfflineData = useCallback(async (key: string) => {
    return await pwaService.getOfflineData(key)
  }, [])

  const clearOfflineData = useCallback(async () => {
    await pwaService.clearOfflineData()
    setHasOfflineData(false)
  }, [])

  return {
    isOnline,
    isOffline: !isOnline,
    hasOfflineData,
    storeOfflineData,
    getOfflineData,
    clearOfflineData
  }
}

// Hook for managing push notifications
export const usePushNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const [isSubscribing, setIsSubscribing] = useState(false)

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])

  const requestPermission = useCallback(async () => {
    const newPermission = await pwaService.requestNotificationPermission()
    setPermission(newPermission)
    return newPermission === 'granted'
  }, [])

  const subscribe = useCallback(async () => {
    if (permission !== 'granted' || isSubscribing) {
      return null
    }

    setIsSubscribing(true)
    
    try {
      const pushSubscription = await pwaService.subscribeToPushNotifications()
      setSubscription(pushSubscription)
      return pushSubscription
    } finally {
      setIsSubscribing(false)
    }
  }, [permission, isSubscribing])

  const unsubscribe = useCallback(async () => {
    if (!subscription) {
      return false
    }

    try {
      const success = await subscription.unsubscribe()
      if (success) {
        setSubscription(null)
      }
      return success
    } catch (error) {
      console.error('Failed to unsubscribe:', error)
      return false
    }
  }, [subscription])

  return {
    permission,
    subscription,
    isSubscribed: subscription !== null,
    isSubscribing,
    canSubscribe: permission === 'granted',
    requestPermission,
    subscribe,
    unsubscribe
  }
}

// Hook for PWA-specific task acknowledgments with offline support
export const usePWATaskAcknowledgment = () => {
  const { isOnline, storeOfflineData } = useOffline()
  const [offlineAcks, setOfflineAcks] = useState<Array<{
    taskId: string
    notes?: string
    timestamp: number
  }>>([])

  const acknowledgeTask = useCallback(async (
    taskId: string, 
    notes?: string,
    apiCall?: (taskId: string, notes?: string) => Promise<void>
  ) => {
    const acknowledgment = {
      taskId,
      notes,
      timestamp: Date.now()
    }

    if (isOnline && apiCall) {
      try {
        // Try to acknowledge online
        await apiCall(taskId, notes)
        return true
      } catch (error) {
        console.error('Online acknowledgment failed, storing offline:', error)
      }
    }

    // Store for offline sync
    await storeOfflineData(`ack-${taskId}`, acknowledgment)
    setOfflineAcks(prev => [...prev, acknowledgment])
    
    // Register for background sync if supported
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      const registration = await navigator.serviceWorker.ready
      await registration.sync.register('task-acknowledgment')
    }

    return false // Indicates offline storage
  }, [isOnline, storeOfflineData])

  return {
    acknowledgeTask,
    offlineAcks,
    hasOfflineAcks: offlineAcks.length > 0
  }
}

// Hook for PWA app info and diagnostics
export const usePWAInfo = () => {
  const [version, setVersion] = useState<string>('unknown')
  const [lastUpdate, setLastUpdate] = useState<string | null>(null)

  useEffect(() => {
    const loadInfo = async () => {
      const appVersion = await pwaService.getVersion()
      setVersion(appVersion)
      
      const updateTime = localStorage.getItem('pwa-last-update')
      setLastUpdate(updateTime)
    }

    loadInfo()
  }, [])

  const refreshInfo = useCallback(async () => {
    const appVersion = await pwaService.getVersion()
    setVersion(appVersion)
    
    const now = new Date().toISOString()
    localStorage.setItem('pwa-last-update', now)
    setLastUpdate(now)
  }, [])

  return {
    version,
    lastUpdate: lastUpdate ? new Date(lastUpdate) : null,
    refreshInfo
  }
}